import { useMemo, useState } from 'react';
import { AsyncBoundary, Drawer, Notice, useToast } from '@kollez/design-system';
import {
  useOrgTree,
  useCreateChildOrg,
  usePendingInvites,
  useResendTenantInvite,
  useRevokeTenantInvite,
  allowedChildKinds,
  type OrgKind,
  type OrgTreeNode,
} from '@/lib/queries';

/**
 * /group/structure — the tenant subtree page. Reads from
 * `GET /tenant/orgs/tree` so any tenant admin (group_admin,
 * university_admin, principal) sees their own subtree. "+ Add" buttons
 * appear on each node for the kinds the caller is authorized to
 * create under that node (per the authority table in `queries.ts`).
 *
 * Creating a child fires `POST /tenant/orgs/{universities|colleges|
 * departments}` which provisions:
 *   • a child Tenant
 *   • an admin User (isActive:false)
 *   • a TenantInvite + activation email (support@kollez.ai → 7 days)
 *   • the domain-side row (University / College). For a DEPARTMENT
 *     under a COLLEGE, the server auto-resolves the collegeId.
 */

const KIND_LABEL: Record<OrgKind, string> = {
  GROUP: 'Group',
  UNIVERSITY: 'University',
  COLLEGE: 'College',
  DEPARTMENT: 'Department',
};

const KIND_HELPER: Record<OrgKind, string> = {
  GROUP: 'root organization',
  UNIVERSITY: 'affiliated + constituent colleges live under this',
  COLLEGE: 'a single campus',
  DEPARTMENT: 'a department inside a college',
};

const CHILD_ROLE_LABEL: Record<OrgKind, string> = {
  GROUP: 'group_admin',
  UNIVERSITY: 'university_admin',
  COLLEGE: 'principal',
  DEPARTMENT: 'hod',
};

export function GroupStructurePage() {
  const toast = useToast();
  const tree = useOrgTree();
  const create = useCreateChildOrg();
  const [addUnder, setAddUnder] = useState<OrgTreeNode | null>(null);

  const root = tree.data?.tree;
  const totals = useMemo(() => {
    const c = { UNIVERSITY: 0, COLLEGE: 0, DEPARTMENT: 0 };
    if (!root) return c;
    const walk = (n: OrgTreeNode) => {
      if (n.kind !== 'GROUP') c[n.kind] += 1;
      for (const child of n.children) walk(child);
    };
    for (const child of root.children) walk(child);
    return c;
  }, [root]);

  async function submitAdd(kind: OrgKind, form: AddForm) {
    if (!addUnder) return;
    try {
      const res = await create.mutateAsync({
        childKind: kind,
        body: {
          name: form.name.trim(),
          adminName: form.adminName.trim(),
          adminEmail: form.adminEmail.trim().toLowerCase(),
          ...(form.aisheCode.trim() ? { aisheCode: form.aisheCode.trim() } : {}),
          ...(form.city.trim() ? { city: form.city.trim() } : {}),
        },
      });
      const emailBit = res.invite.emailDelivered
        ? `— invite emailed to ${res.adminEmail}`
        : `— invite created (email pending)`;
      toast.show({ message: `${KIND_LABEL[kind]} "${res.tenantName}" added ${emailBit}.` });
      setAddUnder(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not create';
      toast.show({ tone: 'warning', message: msg });
    }
  }

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Institutions</p>
          <h1 className="h-page">Your organization tree</h1>
          <p className="subhead">
            {root
              ? `Group → University → College → Department. Add children at any level you administer.`
              : 'Loading your tenant tree.'}
          </p>
        </div>
      </div>

      {root && (
        <div className="grid gap-3 grid-cols-3">
          <div className="card kpi">
            <span className="kpi-label">Universities</span>
            <span className="kpi-value">{totals.UNIVERSITY}</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Colleges</span>
            <span className="kpi-value">{totals.COLLEGE}</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Departments</span>
            <span className="kpi-value">{totals.DEPARTMENT}</span>
          </div>
        </div>
      )}

      {/* Empty-state nudge — a fresh tenant with no children should be
          told where to start, not just presented a bare tree. */}
      {root && root.children.length === 0 && allowedChildKinds(root.kind).length > 0 && (
        <Notice tone="warning">
          <b>Nothing under this tenant yet.</b>{' '}
          Add your first {allowedChildKinds(root.kind)[0]!.toLowerCase()} to unlock the workspace.
          Use the <b>+ Add child</b> button next to the root node above.
        </Notice>
      )}

      <Notice tone="info">
        <b>How this works.</b> Adding a child here creates a new <b>sub-org</b> under your group (University, College or Department) and emails an activation link to the admin you name. The link is valid for 7 days. Sub-orgs roll up under this group's subscription — no separate billing tenant is created.
      </Notice>

      <AsyncBoundary
        kind="detail"
        loading={tree.isLoading}
        error={tree.error}
        empty={!root}
        emptyTitle="No tenant to render"
        onRetry={() => void tree.refetch()}
      >
        {root && <TreeNode node={root} onAddUnder={setAddUnder} />}
      </AsyncBoundary>

      <PendingInvitesSection />

      {addUnder && (
        <AddOrgDrawer
          parent={addUnder}
          onClose={() => setAddUnder(null)}
          onSubmit={submitAdd}
          submitting={create.isPending}
        />
      )}
    </div>
  );
}

/**
 * Pending admin invites the caller has issued anywhere in their subtree.
 * Hidden entirely when there are no pending invites — no "0 rows" table.
 */
function PendingInvitesSection() {
  const toast = useToast();
  const invites = usePendingInvites();
  const resend = useResendTenantInvite();
  const revoke = useRevokeTenantInvite();
  const rows = invites.data?.rows ?? [];
  if (rows.length === 0) return null;

  async function doResend(id: string, email: string) {
    try {
      await resend.mutateAsync(id);
      toast.show({ message: `Invite resent to ${email}` });
    } catch (err) {
      toast.show({ tone: 'warning', message: err instanceof Error ? err.message : 'Could not resend invite' });
    }
  }
  async function doRevoke(id: string, email: string) {
    if (!window.confirm(`Revoke invite for ${email}? The activation link will stop working.`)) return;
    try {
      await revoke.mutateAsync(id);
      toast.show({ message: `Invite for ${email} revoked` });
    } catch (err) {
      toast.show({ tone: 'warning', message: err instanceof Error ? err.message : 'Could not revoke invite' });
    }
  }

  return (
    <section className="card" data-testid="pending-invites">
      <div className="card-head">
        <h2 className="h-section">Pending admin invites</h2>
        <span className="helper">{rows.length} unaccepted</span>
      </div>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Email</th>
              <th className="hide-mobile">Role</th>
              <th>Tenant</th>
              <th className="r">Expires</th>
              <th className="r">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const busy = (resend.isPending && resend.variables === r._id)
                || (revoke.isPending && revoke.variables === r._id);
              return (
                <tr key={r._id}>
                  <td>
                    <div className="cell-title truncate">{r.email}</div>
                    {r.fullName && <div className="cell-sub truncate">{r.fullName}</div>}
                  </td>
                  <td className="hide-mobile">
                    <span className="badge">{r.role.replace(/_/g, ' ')}</span>
                  </td>
                  <td>
                    <div className="cell-title truncate">{r.tenantName}</div>
                    <div className="cell-sub">{r.tenantKind}</div>
                  </td>
                  <td className="r">
                    <span className={`badge ${r.expiresInDays <= 1 ? 'badge-danger' : r.expiresInDays <= 3 ? 'badge-warning' : ''}`}>
                      {r.expiresInDays === 0 ? 'today' : `${r.expiresInDays}d`}
                    </span>
                  </td>
                  <td className="r">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={busy}
                        onClick={() => void doResend(r._id, r.email)}
                        data-testid={`invite-resend-${r._id}`}
                      >
                        Resend
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={busy}
                        onClick={() => void doRevoke(r._id, r.email)}
                        data-testid={`invite-revoke-${r._id}`}
                      >
                        Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Recursive count of every descendant beneath a node — used for the
 * summary line on a collapsed parent ("The ICFAI University, Dehradun ·
 * 5 departments below"). Direct-children count alone underreports
 * because departments hang off colleges hang off universities.
 */
function countDescendants(node: OrgTreeNode): number {
  let n = node.children.length;
  for (const c of node.children) n += countDescendants(c);
  return n;
}

/**
 * A collapsible tree row.
 *
 * The GROUP root defaults to expanded — you want to see immediate
 * universities/colleges when you open the page. Every other node
 * defaults to collapsed so a 19-child ICFAI-shaped group doesn't
 * flood the viewport. The caret is only rendered when there are
 * children to reveal.
 */
function TreeNode({
  node,
  onAddUnder,
  defaultExpanded,
}: {
  node: OrgTreeNode;
  onAddUnder: (n: OrgTreeNode) => void;
  defaultExpanded?: boolean;
}) {
  const hasChildren = node.children.length > 0;
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded ?? node.kind === 'GROUP');
  const allowed = allowedChildKinds(node.kind);
  const descendantCount = hasChildren ? countDescendants(node) : 0;

  return (
    <div style={{ marginLeft: node.level === 0 ? 0 : 24 }}>
      <div
        className="flex items-center gap-3 p-3 border border-line-tertiary rounded mb-2"
        data-testid={`org-node-${node._id}`}
      >
        {/* Caret — space is reserved even when no children so kind
            badges align across siblings with and without children. */}
        {hasChildren ? (
          <button
            type="button"
            aria-label={expanded ? `Collapse ${node.name}` : `Expand ${node.name}`}
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            className="btn btn-ghost btn-xs"
            style={{ padding: '4px 6px', minWidth: 26 }}
            data-testid={`org-toggle-${node._id}`}
          >
            <span
              style={{
                display: 'inline-block',
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 120ms ease',
                fontSize: '0.8rem',
              }}
            >
              ▶
            </span>
          </button>
        ) : (
          <span style={{ display: 'inline-block', minWidth: 26 }} />
        )}
        <span
          className="text-xs uppercase tracking-wide"
          style={{ color: 'rgb(var(--ink-tertiary))', width: 96 }}
        >
          {KIND_LABEL[node.kind]}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{node.name}</div>
          <div className="text-xs" style={{ color: 'rgb(var(--ink-tertiary))' }}>
            {node.adminCount} admin{node.adminCount === 1 ? '' : 's'}
            {hasChildren && (
              <>
                {' · '}
                {node.children.length} direct
                {descendantCount > node.children.length && ` · ${descendantCount} total below`}
              </>
            )}
          </div>
        </div>
        {allowed.length > 0 && (
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => onAddUnder(node)}
            data-testid={`org-add-${node._id}`}
          >
            + Add child
          </button>
        )}
      </div>
      {expanded && node.children.map((c) => (
        <TreeNode key={c._id} node={c} onAddUnder={onAddUnder} />
      ))}
    </div>
  );
}

interface AddForm {
  name: string;
  adminName: string;
  adminEmail: string;
  aisheCode: string;
  city: string;
}

/**
 * Uses the Drawer's built-in `footer` prop for sticky Cancel/Create.
 * The old inline `.drawer-foot` inside children scrolled with the body
 * — on 720-px viewports the primary CTA disappeared below the fold
 * with no scroll affordance. Sticky footer keeps it always visible.
 */
function AddOrgDrawer({
  parent,
  onClose,
  onSubmit,
  submitting,
}: {
  parent: OrgTreeNode;
  onClose: () => void;
  onSubmit: (kind: OrgKind, form: AddForm) => void;
  submitting: boolean;
}) {
  const allowed = allowedChildKinds(parent.kind);
  const [kind, setKind] = useState<OrgKind>(allowed[0] ?? 'DEPARTMENT');
  const [form, setForm] = useState<AddForm>({ name: '', adminName: '', adminEmail: '', aisheCode: '', city: '' });
  const canSubmit = form.name.trim().length >= 2 && form.adminName.trim().length >= 2 && /^\S+@\S+\.\S+$/.test(form.adminEmail);

  return (
    <Drawer
      open
      onClose={onClose}
      title={`Add under ${parent.name}`}
      size="md"
      footer={
        <>
          <button className="btn btn-outline" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            className="btn btn-primary ml-auto"
            disabled={!canSubmit || submitting}
            onClick={() => onSubmit(kind, form)}
            data-testid="add-org-submit"
          >
            {submitting ? 'Creating…' : `Create ${KIND_LABEL[kind]}`}
          </button>
        </>
      }
    >
      <div className="grid gap-4">
        <div className="grid gap-2">
          <label className="label">Kind</label>
          <div className="flex gap-2 flex-wrap">
            {allowed.map((k) => (
              <button
                key={k}
                type="button"
                className={`tag ${kind === k ? 'tag-active' : ''}`}
                aria-pressed={kind === k}
                onClick={() => setKind(k)}
              >
                {KIND_LABEL[k]}
              </button>
            ))}
          </div>
          <p className="helper m-0">{KIND_HELPER[kind]}</p>
        </div>
        <label className="grid gap-1.5">
          <span className="label">Name</span>
          <input
            className="input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder={kind === 'UNIVERSITY' ? 'e.g. State Technical University' : kind === 'COLLEGE' ? 'e.g. Coastal Engineering College' : 'e.g. Mechanical Engineering'}
            data-testid="add-org-name"
          />
        </label>
        {kind !== 'DEPARTMENT' && (
          <>
            <label className="grid gap-1.5">
              <span className="label">AISHE code <span className="opt">(optional)</span></span>
              <input
                className="input"
                value={form.aisheCode}
                onChange={(e) => setForm((f) => ({ ...f, aisheCode: e.target.value }))}
                placeholder="e.g. C-51230"
              />
            </label>
            <label className="grid gap-1.5">
              <span className="label">City <span className="opt">(optional)</span></span>
              <input
                className="input"
                value={form.city}
                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                placeholder="e.g. Vishakhapatnam"
              />
            </label>
          </>
        )}
        <div className="grid gap-1.5">
          <span className="label">Admin</span>
          <p className="helper m-0" style={{ marginBottom: 4 }}>
            They'll be added as <b>{CHILD_ROLE_LABEL[kind].replace(/_/g, ' ')}</b> and get an activation email.
          </p>
        </div>
        <label className="grid gap-1.5">
          <span className="label">Admin full name</span>
          <input
            className="input"
            value={form.adminName}
            onChange={(e) => setForm((f) => ({ ...f, adminName: e.target.value }))}
            data-testid="add-org-admin-name"
          />
        </label>
        <label className="grid gap-1.5">
          <span className="label">Admin work email</span>
          <input
            className="input"
            type="email"
            value={form.adminEmail}
            onChange={(e) => setForm((f) => ({ ...f, adminEmail: e.target.value }))}
            data-testid="add-org-admin-email"
          />
        </label>
      </div>
    </Drawer>
  );
}
